import { StepHelpers } from "@shared/components/Stepper";
import { Button } from "@shared/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import { Badge } from "@shared/ui/badge";
import { Textarea } from "@shared/ui/textarea";
import { AlertTriangle, CheckCircle2, FileText, Package } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useBulkCreateWorkOrderJobCard } from "@features/mx/device-management/hooks";
import { ValueLabel } from "@features/ax/shared/ValueLabel";
import { ServiceOrderSuccessDialog } from "./ServiceOrderSuccessDialog";
import { getStatusBadgeClass } from "@shared/utils/statusUtils";

interface ReviewStepProps {
  data?: any;
  onDataChange?: (data: any) => void;
  onNext?: () => void;
  onPrevious?: () => void;
  isFirstStep?: boolean;
  isLastStep?: boolean;
  currentStepIndex?: number;
  totalSteps?: number;
  setValidationError?: (error: string) => void;
  clearValidationError?: () => void;
  stepHelpers?: StepHelpers;
  allStepData?: any;
  remoteUtilityId?: string;
}

export const ReviewStep = ({
  data = {},
  onDataChange,
  onNext,
  onPrevious,
  isFirstStep = false,
  isLastStep = true,
  currentStepIndex = 2,
  totalSteps = 3,
  setValidationError,
  clearValidationError,
  stepHelpers,
  allStepData,
  remoteUtilityId,
}: ReviewStepProps) => {
  const navigate = useNavigate();
  const [notes, setNotes] = useState(data.notes || "");
  const [showMeterDetails, setShowMeterDetails] = useState(false);

  // API mutation hook
  const { mutate: createJobCards, isPending: isSubmitting } =
    useBulkCreateWorkOrderJobCard();

  // Get data from previous steps using step helpers
  const serviceDetails = useMemo(() => {
    if (stepHelpers) {
      return stepHelpers.getStepData(0);
    }
    return allStepData?.[0] || {};
  }, [stepHelpers, allStepData]);

  const meterSelection = useMemo(() => {
    if (stepHelpers) {
      return stepHelpers.getStepData(1);
    }
    return allStepData?.[1] || {};
  }, [stepHelpers, allStepData]);

  // Update notes in step data when changed
  useEffect(() => {
    if (notes !== data.notes) {
      onDataChange?.({ ...data, notes });
    }
  }, [notes]);

  // Get operation type display name
  const getOperationTypeDisplay = (operationType: string) => {
    const operationMap: Record<string, string> = {
      "meter-installation": "Meter Installation",
      "meter-replacement": "Meter Replacement",
      "meter-disposal": "Meter Disposal",
      "meter-inspection": "Meter Inspection",
      "meter-calibration": "Meter Calibration",
    };
    return operationMap[operationType] || operationType;
  };

  // Get SOP display name
  const getSopDisplay = (sop: string) => {
    const sopMap: Record<string, string> = {
      "standard-installation": "Standard Installation SOP",
      "complex-installation": "Complex Installation SOP",
      "emergency-replacement": "Emergency Meter Replacement",
      "standard-replacement": "Standard Meter Replacement",
      "upgrade-replacement": "Upgrade Replacement",
      "standard-disposal": "Standard Disposal SOP",
      "hazardous-disposal": "Hazardous Material Disposal",
      "routine-inspection": "Routine Inspection SOP",
      "detailed-inspection": "Detailed Inspection SOP",
      "standard-calibration": "Standard Calibration SOP",
      "precision-calibration": "Precision Calibration SOP",
    };
    return sopMap[sop] || sop;
  };

  // Get priority badge variant
  const getPriorityBadgeVariant = (priority: string) => {
    switch (priority) {
      case "critical":
        return "destructive";
      case "high":
        return "default";
      case "medium":
        return "secondary";
      case "low":
        return "outline";
      default:
        return "outline";
    }
  };
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [createdCount, setCreatedCount] = useState(0);

  const handleCreateServiceOrders = async () => {
    clearValidationError?.();

    if (
      !meterSelection?.selectedMeterCount ||
      meterSelection.selectedMeterCount === 0
    ) {
      setValidationError?.(
        "Please select at least one meter to create service orders."
      );
      return;
    }

    if (!serviceDetails?.operationType) {
      setValidationError?.(
        "Operation type is required. Please go back to Step 1."
      );
      return;
    }

    try {
      const payload = {
        operationType: serviceDetails.operationType,
        priority: serviceDetails.priority,
        sop: serviceDetails.sop,
        remote_utility_id: remoteUtilityId,
        meter_ids:
          meterSelection.selectedMeters?.map((meter: any) => meter.id) || [],
        selectedMeterCount: meterSelection.selectedMeterCount,
        remark: notes || "",
      };

      console.log("Creating service orders with payload:", payload);

      createJobCards(payload, {
        onSuccess: (response) => {
          console.log("Service orders created successfully:", response);

          setCreatedCount(meterSelection.selectedMeterCount || 0);
          setShowSuccessDialog(true);

          // Store notes to current step
          stepHelpers?.setStepData(currentStepIndex, { notes });
        },
        onError: (error: any) => {
          setValidationError?.(
            error?.message ||
              error?.response?.data?.message ||
              "An error occurred while creating service orders. Please try again."
          );
        },
      });
    } catch (error) {
      setValidationError?.("An unexpected error occurred. Please try again.");
    }
  };

  // Handlers for dialog buttons
  const handleStayHere = () => {
    setShowSuccessDialog(false);
    stepHelpers?.resetAllData();
    localStorage.removeItem("create-service-order-progress");
    navigate("/mx/device-management/service-order/create#service-details");
  };

  const handleGoToList = async () => {
    setShowSuccessDialog(false);

    // Clear state first
    if (stepHelpers?.resetAllData) {
      stepHelpers.resetAllData();
    }

    // Clear localStorage
    localStorage.removeItem("create-service-order-progress");

    // Add a slight delay
    await new Promise((resolve) => setTimeout(resolve, 50));

    // Then navigate with replace option to prevent going back to form
    navigate("/mx/device-management/service-order", { replace: true });
  };

  return (
    <div className="space-y-6">
      {/* Service Details Card */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Package className="h-5 w-5 text-blue-600" />
            Service Details
          </CardTitle>
        </CardHeader>

        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* ✅ Operation Type */}
            <ValueLabel
              label="Operation Type"
              value={serviceDetails?.operationTypeName || "Not selected"}
            />

            {/* ✅ Priority with Badge */}
            <div className="flex flex-col gap-0.5">
              <p className="text-xs text-muted-foreground tracking-wide">
                Priority
              </p>
              <span
                className={`text-xs font-medium px-2.5 py-0.5 rounded-full border capitalize w-fit ${getStatusBadgeClass(
                  serviceDetails?.priorityName?.toLowerCase() || "medium"
                )}`}
              >
                {serviceDetails?.priorityName ||
                  serviceDetails?.priority ||
                  "Medium"}
              </span>
            </div>

            {/* ✅ SOP */}
            <ValueLabel
              label="SOP"
              value={serviceDetails?.sopName || "Not selected"}
            />
          </div>
        </CardContent>
      </Card>
      {/* Service Orders to be Created Card */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <FileText className="h-5 w-5 text-blue-600" />
            Service Orders to be Created
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-amber-900">
                {meterSelection?.selectedMeterCount || 0} service order
                {meterSelection?.selectedMeterCount !== 1 ? "s" : ""} will be
                created
              </p>
              <p className="text-xs text-amber-700 mt-1">
                One service order per selected meter
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
      {/* Additional Notes */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Additional Notes (Optional)</CardTitle>
        </CardHeader>
        <CardContent>
          <Textarea
            placeholder="Add any additional notes or instructions for the service orders..."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={4}
            className="resize-none"
          />
          <p className="text-xs text-gray-500 mt-2">
            These notes will be attached to all service orders being created.
          </p>
        </CardContent>
      </Card>
      {/* Navigation Buttons */}
      <div className="flex justify-between pt-6 border-t border-gray-200">
        <Button variant="outline" onClick={onPrevious} disabled={isSubmitting}>
          <svg
            className="w-4 h-4 mr-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          Previous
        </Button>
        <Button
          onClick={handleCreateServiceOrders}
          disabled={
            isSubmitting ||
            !meterSelection?.selectedMeterCount ||
            meterSelection.selectedMeterCount === 0
          }
          className="min-w-[200px]"
        >
          {isSubmitting ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
              Creating...
            </>
          ) : (
            <>
              <CheckCircle2 className="w-4 h-4 mr-2" />
              Create {meterSelection?.selectedMeterCount || 0} Service Order
              {meterSelection?.selectedMeterCount !== 1 ? "s" : ""}
            </>
          )}
        </Button>
      </div>
      <ServiceOrderSuccessDialog
        isOpen={showSuccessDialog}
        onClose={handleStayHere}
        onGoToList={handleGoToList}
        orderCount={createdCount}
      />
    </div>
  );
};
